import { Ionicons } from "@expo/vector-icons";
import React from "react";
import { View, Text, StyleSheet, FlatList } from "react-native";

import { useAppTheme } from "../../../design-system/ThemeProvider";
import { t } from "../../../i18n/translations";
import { COLORS } from "../../../theme/colors";
import type { Player } from '../../../types/multiplayer';
import { useLanguageStore } from "../../quiz-core/languageStore";


export type PlayerStatus = Player;

interface MultiplayerPlayerStatusListProps {
  players: Player[];
}

const MultiplayerPlayerStatusList: React.FC<
  MultiplayerPlayerStatusListProps
> = ({ players }) => {
  const { colors } = useAppTheme();
  const { language } = useLanguageStore();
  if (!players || players.length === 0) return null;
  const finishedCount = players.filter((p) => p.finished).length;
  // Determine if we're in pre-game (no one has finished or started playing yet)
  const isPreGame = players.every((p) => !p.finished && !p.disconnected);
  // Pre-game: show waiting status instead of "finished" count
  const headerText = isPreGame
    ? (players.length === 1
        ? t('multiplayer_lobby.playersWaiting', language).replace('{{count}}', players.length.toString())
        : t('multiplayer_lobby.playersWaitingPlural', language).replace('{{count}}', players.length.toString()))
    : t('multiplayer_lobby.playersFinished', language).replace('{{finished}}', finishedCount.toString()).replace('{{total}}', players.length.toString());

  return (
    <View style={[styles.container, { backgroundColor: colors.surfaceContainerLow, borderColor: colors.outlineVariant }]} data-oid="2uihzzy">
      <Text style={[styles.header, { color: colors.primary }]} data-oid="isdq4f9">
        {headerText}
      </Text>
      <FlatList
        data={players}
        keyExtractor={(item) => item.id}
        renderItem={({ item }) => (
          <View style={styles.playerRow} data-oid="af0s-2b">
            <Ionicons
              name={
                item.finished
                  ? "checkmark-circle"
                  : item.disconnected
                    ? "close-circle"
                    : item.is_ready
                      ? "checkmark-circle-outline"
                      : "hourglass-outline"
              }
              size={22}
              color={
                item.finished
                  ? COLORS.correct
                  : item.disconnected
                    ? COLORS.error
                    : item.is_ready
                      ? '#10B981'
                      : COLORS.primary
              }
              style={{ marginRight: 10 }}
              data-oid="9le4hyd"
            />

            <Text
              style={[
                styles.playerName,
                { color: colors.onSurface },
                item.finished && styles.finishedName,
                item.disconnected && styles.disconnectedName
              ]}
              data-oid="rx5rp_h"
            >
              {item.name}
            </Text>
            <Text style={[styles.statusText, { color: colors.onSurfaceVariant }]} data-oid="-438nhq">
              {item.finished
                ? t('multiplayer_lobby.finished', language)
                : item.disconnected
                  ? t('multiplayer_lobby.disconnected', language)
                  : isPreGame
                    ? (item.is_ready ? t('multiplayer_lobby.ready', language) : t('multiplayer_lobby.waitingStatus', language))
                    : t('multiplayer_lobby.playing', language)}
            </Text>
          </View>
        )}
        data-oid="lviqlr2"
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    marginTop: 16,
    marginBottom: 8,
    borderRadius: 12,
    padding: 14,
    borderWidth: 1,

    },
  header: {
    fontWeight: "bold",
    marginBottom: 8,
    fontSize: 16
  },
  playerRow: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 6
  },
  playerName: {
    fontSize: 15,
    flex: 1
  },
  finishedName: {
    color: COLORS.correct,
    fontWeight: "bold"
  },
  disconnectedName: {
    color: COLORS.error,
    fontStyle: "italic"
  },
  statusText: {
    fontSize: 13,
    marginLeft: 8
  }
});

export default MultiplayerPlayerStatusList;
